import type { MeResponse } from "../api/types";
import { hasAnyPermission, hasPermission } from "../auth/permissions";
import { Link, pathStartsWith } from "../router/Router";

interface NavItem {
  to: string;
  label: string;
  iconKey: IconKey;
  visible: boolean;
  comingSoon?: boolean;
  match?: (path: string) => boolean;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

export function Sidebar({
  me,
  path,
  open,
  onNavigate
}: {
  me: MeResponse;
  path: string;
  open: boolean;
  onNavigate: () => void;
}) {
  const sections = navSections(me);
  return (
    <aside className={`sidebar${open ? " is-open" : ""}`} aria-label="Primary navigation">
      <div className="sidebar__brand">
        <span className="sidebar__crown" aria-hidden="true">♛</span>
        <div className="sidebar__brand-text">
          <span className="sidebar__brand-title">Contribution<br />Blocks</span>
          <span className="sidebar__brand-sub">{me.tenant.name}</span>
        </div>
      </div>
      <nav className="sidebar__nav">
        {sections
          .filter((section) => section.items.some((item) => item.visible))
          .map((section) => (
          <div className="sidebar__section" key={section.label}>
            <div className="sidebar__section-label">{section.label}</div>
            {section.items.map((item) => {
              if (!item.visible) {
                return null;
              }
              const active = item.match !== undefined ? item.match(path) : pathStartsWith(path, item.to);
              if (item.comingSoon === true) {
                return (
                  <span
                    key={item.label}
                    className="sidebar__item sidebar__item--soon"
                    aria-disabled="true"
                    title="Coming soon"
                  >
                    <Icon name={item.iconKey} />
                    <span>{item.label}</span>
                    <span className="sidebar__soon-pill">Soon</span>
                  </span>
                );
              }
              return (
                <Link
                  key={item.label}
                  to={item.to}
                  className={`sidebar__item${active ? " is-active" : ""}`}
                  aria-current={active ? "page" : undefined}
                  onClick={onNavigate}
                >
                  <Icon name={item.iconKey} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>
        ))}
      </nav>
    </aside>
  );
}

function navSections(me: MeResponse): NavSection[] {
  return [
    {
      label: "Overview",
      items: [
        {
          to: "/admin/people",
          label: "People",
          iconKey: "people",
          visible: hasAnyPermission(me, ["members:view", "members:manage"]),
          match: (path) =>
            pathStartsWith(path, "/admin/people") || path === "/admin" || path === "/admin/"
        }
      ]
    },
    {
      label: "Block Ops",
      items: [
        {
          to: "/blocks",
          label: "Dashboard",
          iconKey: "gauge",
          visible: me.features.blocks && hasPermission(me, "blocks:view"),
          match: (path) =>
            path === "/blocks" ||
            path === "/blocks/" ||
            (pathStartsWith(path, "/blocks") &&
              !pathStartsWith(path, "/blocks/mine") &&
              !pathStartsWith(path, "/blocks/all") &&
              !pathStartsWith(path, "/blocks/import") &&
              !pathStartsWith(path, "/blocks/people") &&
              !pathStartsWith(path, "/blocks/approvals") &&
              !pathStartsWith(path, "/blocks/pillars") &&
              !pathStartsWith(path, "/blocks/seasons"))
        },
        {
          to: "/blocks/mine",
          label: "My Blocks",
          iconKey: "blocks",
          visible: me.features.blocks && hasPermission(me, "blocks:view"),
          match: (path) => pathStartsWith(path, "/blocks/mine")
        },
        {
          to: "/blocks/all",
          label: "All Blocks",
          iconKey: "grid",
          visible: me.features.blocks && hasPermission(me, "blocks:view"),
          match: (path) => pathStartsWith(path, "/blocks/all") || pathStartsWith(path, "/blocks/import")
        },
        {
          to: "/blocks/people",
          label: "Contributors",
          iconKey: "people",
          visible: me.features.blocks && hasPermission(me, "blocks:people:view"),
          match: (path) => pathStartsWith(path, "/blocks/people")
        },
        {
          to: "/blocks/approvals",
          label: "Approvals",
          iconKey: "approvals",
          visible: me.features.blocks && hasPermission(me, "blocks:approve_completion"),
          match: (path) => pathStartsWith(path, "/blocks/approvals")
        },
        {
          to: "/blocks/pillars",
          label: "Pillars",
          iconKey: "pillars",
          visible: me.features.blocks && hasPermission(me, "blocks:pillars:manage"),
          match: (path) => pathStartsWith(path, "/blocks/pillars")
        },
        {
          to: "/blocks/seasons",
          label: "Seasons",
          iconKey: "calendar",
          visible: me.features.blocks && hasPermission(me, "blocks:seasons:manage"),
          match: (path) => pathStartsWith(path, "/blocks/seasons")
        },
        {
          to: "/vision",
          label: "Vision",
          iconKey: "vision",
          visible: me.features.blocks && hasPermission(me, "blocks:view")
        }
      ]
    },
    {
      label: "Culture",
      items: [
        {
          to: "/gumballs",
          label: "Gumballs",
          iconKey: "gumball",
          visible: hasPermission(me, "gumballs:view")
        },
        {
          to: "/admin/ledger",
          label: "Ledger",
          iconKey: "ledger",
          visible: hasPermission(me, "gumballs:ledger:view")
        },
        {
          to: "/admin/seasons",
          label: "Gumball Seasons",
          iconKey: "calendar",
          visible: hasPermission(me, "gumballs:season:manage")
        },
        {
          to: "/admin/coin-uploads",
          label: "Coin Uploads",
          iconKey: "upload",
          visible: hasPermission(me, "gumballs:coins:upload")
        },
        {
          to: "/admin/adjustments",
          label: "Adjustments",
          iconKey: "adjust",
          visible: hasPermission(me, "gumballs:balances:adjust")
        }
      ]
    },
    {
      label: "Hiring",
      items: [
        {
          to: "/hiring",
          label: "Open Positions",
          iconKey: "briefcase",
          visible: me.features.hiring && hasPermission(me, "positions:view"),
          match: (path) => pathStartsWith(path, "/hiring") && !pathStartsWith(path, "/hiring/departments")
        },
        {
          to: "/hiring/departments",
          label: "Departments",
          iconKey: "folder",
          visible: me.features.hiring && hasPermission(me, "departments:manage"),
          match: (path) => pathStartsWith(path, "/hiring/departments")
        }
      ]
    },
    {
      label: "Workspace",
      items: [
        {
          to: "/admin/themes",
          label: "Themes",
          iconKey: "palette",
          visible: hasPermission(me, "settings:manage")
        }
      ]
    }
  ];
}

type IconKey =
  | "people"
  | "blocks"
  | "gauge"
  | "grid"
  | "pillars"
  | "approvals"
  | "vision"
  | "gumball"
  | "ledger"
  | "calendar"
  | "upload"
  | "adjust"
  | "palette"
  | "briefcase"
  | "folder";

function Icon({ name }: { name: IconKey }) {
  switch (name) {
    case "people":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );
    case "blocks":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        </svg>
      );
    case "gauge":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M4 18a8 8 0 1 1 16 0" />
          <path d="M12 18l4.5-5" />
          <circle cx="12" cy="18" r="1.5" />
        </svg>
      );
    case "grid":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
        </svg>
      );
    case "pillars":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M3 6h18" />
          <path d="M4 20h16" />
          <line x1="6" y1="6" x2="6" y2="20" />
          <line x1="12" y1="6" x2="12" y2="20" />
          <line x1="18" y1="6" x2="18" y2="20" />
        </svg>
      );
    case "approvals":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <polyline points="9 11 12 14 22 4" />
          <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
        </svg>
      );
    case "vision":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7S2 12 2 12z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      );
    case "gumball":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="12" r="4" />
        </svg>
      );
    case "ledger":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M4 4h16v16H4z" />
          <path d="M4 9h16M4 15h16M9 4v16" />
        </svg>
      );
    case "calendar":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M16 2v4M8 2v4M3 10h18" />
        </svg>
      );
    case "upload":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
      );
    case "adjust":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <line x1="4" y1="21" x2="4" y2="14" />
          <line x1="4" y1="10" x2="4" y2="3" />
          <line x1="12" y1="21" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12" y2="3" />
          <line x1="20" y1="21" x2="20" y2="16" />
          <line x1="20" y1="12" x2="20" y2="3" />
          <line x1="1" y1="14" x2="7" y2="14" />
          <line x1="9" y1="8" x2="15" y2="8" />
          <line x1="17" y1="16" x2="23" y2="16" />
        </svg>
      );
    case "palette":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M12 2a10 10 0 1 0 0 20c1.1 0 2-.9 2-2 0-.5-.2-1-.5-1.4-.3-.4-.5-.9-.5-1.4 0-1.1.9-2 2-2h2.5a4.5 4.5 0 0 0 4.5-4.5C22 6.1 17.5 2 12 2z" />
          <circle cx="7.5" cy="10.5" r="1" />
          <circle cx="12" cy="7.5" r="1" />
          <circle cx="16.5" cy="10.5" r="1" />
        </svg>
      );
    case "briefcase":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <rect x="2" y="7" width="20" height="14" rx="2" />
          <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
        </svg>
      );
    case "folder":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
        </svg>
      );
  }
}
